import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchDashboardStats, DashboardStats } from '@/lib/api';

const STAT_CARDS = [
  { key: 'leaders', label: 'Leaders', icon: 'ri-user-star-line', to: '/admin/leaders' },
  { key: 'sermons', label: 'Sermons', icon: 'ri-mic-line', to: '/admin/sermons' },
  { key: 'events', label: 'Events', icon: 'ri-calendar-event-line', to: '/admin/events' },
  { key: 'news', label: 'Articles', icon: 'ri-newspaper-line', to: '/admin/news' },
  { key: 'gallery', label: 'Gallery', icon: 'ri-image-line', to: '/admin/gallery' },
  { key: 'testimonials', label: 'Testimonials', icon: 'ri-chat-quote-line', to: '/admin/testimonials' },
  { key: 'ministries', label: 'Ministries', icon: 'ri-heart-3-line', to: '/admin/ministries' },
  { key: 'prayer_requests', label: 'Prayer Requests', icon: 'ri-hand-heart-line', to: '/admin/inbox' },
  { key: 'contact_submissions', label: 'Contact Messages', icon: 'ri-mail-line', to: '/admin/inbox' },
  { key: 'newsletter_subscribers', label: 'Subscribers', icon: 'ri-mail-send-line', to: '/admin/inbox' },
] as const;

export default function DashboardOverview() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats()
      .then(setStats)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">Welcome back</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Here's an overview of your website content. Click any card to manage it.
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
        </div>
      ) : error ? (
        <div className="px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {STAT_CARDS.map((card) => (
            <Link
              key={card.key}
              to={card.to}
              className="bg-white rounded-lg border border-background-200 p-5 hover:border-primary-300 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className={`${card.icon} text-lg`} aria-hidden="true" />
                </span>
                <i
                  className="ri-arrow-right-line text-foreground-300 group-hover:text-primary-500 transition-colors"
                  aria-hidden="true"
                />
              </div>
              <div className="font-heading font-bold text-3xl text-foreground-900">
                {stats?.[card.key] ?? 0}
              </div>
              <div className="text-xs text-foreground-500 mt-1">{card.label}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="mt-10 bg-white rounded-lg border border-background-200 p-6">
        <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Edit Church Info', to: '/admin/settings', icon: 'ri-settings-3-line' },
            { label: 'Add Leader', to: '/admin/leaders', icon: 'ri-user-add-line' },
            { label: 'Add Sermon', to: '/admin/sermons', icon: 'ri-mic-line' },
            { label: 'Add Event', to: '/admin/events', icon: 'ri-calendar-event-line' },
            { label: 'Add Article', to: '/admin/news', icon: 'ri-newspaper-line' },
            { label: 'Add Photo', to: '/admin/gallery', icon: 'ri-image-add-line' },
            { label: 'Review Requests', to: '/admin/inbox', icon: 'ri-inbox-line' },
          ].map((a) => (
            <Link
              key={a.label}
              to={a.to}
              className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-700 transition-colors"
            >
              <i className={a.icon} aria-hidden="true" />
              {a.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}